import { useState, useCallback, useEffect, useRef } from 'react';
import {
  useGetAdminTagsQuery,
  useCreateTagMutation,
  useUpdateTagMutation,
  useDeleteTagMutation,
  useReorderTagsMutation,
} from '@/store/api/corkBoard/corkBoardApi';
import { useAdminUser } from './useAdminUser';
import { useToast } from '@/contexts/admin/ToastContext';
import type { CorkBoardTag, CreateTagRequest, UpdateTagRequest } from '@/types/residents/corkBoard';

const REORDER_DEBOUNCE_MS = 500;

export const useTagManagement = () => {
  const adminUser = useAdminUser();
  const selectedPropertyId = adminUser.selectedPropertyId;
  const { showSuccess, showError } = useToast();

  const {
    data: serverTags,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetAdminTagsQuery(
    { propertyId: selectedPropertyId!, includeDisabled: true },
    { skip: !selectedPropertyId },
  );

  const [createTagMutation, { isLoading: isCreating }] = useCreateTagMutation();
  const [updateTagMutation, { isLoading: isUpdating }] = useUpdateTagMutation();
  const [deleteTagMutation, { isLoading: isDeleting }] = useDeleteTagMutation();
  const [reorderTagsMutation] = useReorderTagsMutation();

  // Local state for optimistic reorder
  const [localTags, setLocalTags] = useState<CorkBoardTag[]>([]);
  const localTagsRef = useRef<CorkBoardTag[]>(localTags);
  const reorderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const serverTagsRef = useRef(serverTags);
  localTagsRef.current = localTags;

  useEffect(() => {
    serverTagsRef.current = serverTags;
    if (serverTags) {
      setLocalTags([...serverTags].sort((a, b) => a.position - b.position));
    }
  }, [serverTags]);

  useEffect(() => {
    return () => {
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);
    };
  }, []);

  const isMutating = isCreating || isUpdating || isDeleting;

  const handleCreateTag = useCallback(
    async (data: Omit<CreateTagRequest, 'propertyId'>) => {
      if (!selectedPropertyId) return false;
      try {
        await createTagMutation({ ...data, propertyId: selectedPropertyId }).unwrap();
        showSuccess('Tag created successfully');
        return true;
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to create tag';
        showError(message);
        return false;
      }
    },
    [selectedPropertyId, createTagMutation, showSuccess, showError],
  );

  const handleUpdateTag = useCallback(
    async (tagId: number, data: Omit<UpdateTagRequest, 'tagId'>) => {
      try {
        await updateTagMutation({ tagId, ...data }).unwrap();
        showSuccess('Tag updated successfully');
        return true;
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to update tag';
        showError(message);
        return false;
      }
    },
    [updateTagMutation, showSuccess, showError],
  );

  const handleToggleEnabled = useCallback(
    async (tag: CorkBoardTag) => {
      setLocalTags((prev) =>
        prev.map((t) => (t.id === tag.id ? { ...t, isEnabled: !t.isEnabled } : t)),
      );
      try {
        await updateTagMutation({ tagId: tag.id, isEnabled: !tag.isEnabled }).unwrap();
        showSuccess(`Tag ${tag.isEnabled ? 'disabled' : 'enabled'} successfully`);
        return true;
      } catch (err: any) {
        setLocalTags((prev) =>
          prev.map((t) => (t.id === tag.id ? { ...t, isEnabled: tag.isEnabled } : t)),
        );
        const message = err?.data?.message || 'Failed to update tag';
        showError(message);
        return false;
      }
    },
    [updateTagMutation, showSuccess, showError],
  );

  const handleDeleteTag = useCallback(
    async (tagId: number) => {
      try {
        await deleteTagMutation(tagId).unwrap();
        showSuccess('Tag deleted successfully');
        return true;
      } catch (err: any) {
        const message = err?.data?.message || 'Failed to delete tag';
        showError(message);
        return false;
      }
    },
    [deleteTagMutation, showSuccess, showError],
  );

  const persistReorder = useCallback(
    (reorderedTags: CorkBoardTag[]) => {
      if (!selectedPropertyId) return;
      if (reorderTimerRef.current) clearTimeout(reorderTimerRef.current);

      reorderTimerRef.current = setTimeout(async () => {
        const tagIds = reorderedTags.map((t) => t.id);
        try {
          await reorderTagsMutation({ propertyId: selectedPropertyId, tagIds }).unwrap();
        } catch {
          const fallback = serverTagsRef.current;
          if (fallback) {
            setLocalTags([...fallback].sort((a, b) => a.position - b.position));
          }
          showError('Failed to reorder tags');
        }
      }, REORDER_DEBOUNCE_MS);
    },
    [selectedPropertyId, reorderTagsMutation, showError],
  );

  const handleDragEnd = useCallback(
    ({ data: reordered }: { data: CorkBoardTag[] }) => {
      setLocalTags(reordered);
      persistReorder(reordered);
    },
    [persistReorder],
  );

  const handleMoveUp = useCallback(
    (tagId: number) => {
      const tags = localTagsRef.current;
      const index = tags.findIndex((t) => t.id === tagId);
      if (index <= 0) return;
      const updated = [...tags];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      setLocalTags(updated);
      persistReorder(updated);
    },
    [persistReorder],
  );

  const handleMoveDown = useCallback(
    (tagId: number) => {
      const tags = localTagsRef.current;
      const index = tags.findIndex((t) => t.id === tagId);
      if (index < 0 || index >= tags.length - 1) return;
      const updated = [...tags];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      setLocalTags(updated);
      persistReorder(updated);
    },
    [persistReorder],
  );

  return {
    tags: localTags,
    isLoading,
    isFetching,
    isMutating,
    isCreating,
    isUpdating,
    isDeleting,
    error,
    selectedPropertyId,
    refetch,
    handleCreateTag,
    handleUpdateTag,
    handleToggleEnabled,
    handleDeleteTag,
    handleDragEnd,
    handleMoveUp,
    handleMoveDown,
  };
};
